import {
  IFormJBPHPContactChangeRequest,
  IFormJBPHPContactRoles,
  newHpContactRequest,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { HPContactsDuplicateDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsDuplicateDialog/HPContactsDuplicateDialog'
import { saveHPContactChangeRequest } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/saveHPContactChangeRequest'
import { useHpContacts } from 'legacy/lut/components/widgets/public/partner360/hooks/hpContacts/useHpContacts'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { guid } from 'core/classes/helpers/GuidHelper'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { HPContactRequestEditor } from 'partner360/features/hpContacts'
import { getHPContactRoleForEditing } from 'partner360/features/hpContacts/model/getHPContactRoleForEditing'
import { TRANSLATION_KEYS } from 'partner360/widgets/ramManagerAssignment/translations'

interface ICreateHpContactProps {
  partnerId: string
  leadPbmId: number
  allRoles: IFormJBPHPContactRoles[]
  crudKey: string
  fetchPartnerRoles: (value: boolean) => Promise<IFormJBPHPContactRoles[]>
  role?: string
  onUpdateWizard: () => void
}

export const CreateHpContact = ({
  partnerId,
  leadPbmId,
  allRoles,
  crudKey,
  fetchPartnerRoles,
  role,
  onUpdateWizard,
}: ICreateHpContactProps) => {
  const user = useSelector(selectUser)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const isEditMode = useSelector(selectPublishingMode)

  const [showContactTicketDialog, setShowContactTicketDialog] = useState(false)
  const [currentHPContact, setCurrentHPContact] = useState<IFormJBPHPContactChangeRequest>()
  const [isDuplicationCheckInProgress, setDuplicationCheckInProgress] = useState(false)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)

  const { jbpHpContactChangeRequestCrud, resetCrudStorage, getContactsByEmail } = useHpContacts({
    crudKey: crudKey,
    partnerId,
  })

  const onButtonClick = useCallback(() => {
    setCurrentHPContact({
      ...newHpContactRequest,

      FirstName: user.firstName,
      LastName: user.lastName,
      Email: user.email,
      SignId: `new-${guid()}`,

      Roles: getHPContactRoleForEditing({
        isDistributor,
        currentDefaultHpContactRole: '',
        currentRole: role || '',
      }),

      RequestAction: 'Add',
    })

    setShowContactTicketDialog(true)
  }, [isDistributor, role, user.email, user.firstName, user.lastName])

  return (
    <div>
      <LoaderOverlay isVisible={isDuplicationCheckInProgress} />
      <RenderWhen condition={isEditMode}>
        {role ? (
          <Tooltip tipContent={() => <Translation text={TRANSLATION_KEYS.addNewContactTooltip} />}>
            <Button
              variant={'text'}
              iconButton="kbase kbase-add kbase-sm"
              onClick={onButtonClick}
            />
          </Tooltip>
        ) : (
          <Button margin={'xl'} onClick={onButtonClick}>
            <Translation text={TRANSLATION_KEYS.addNewContact} />
          </Button>
        )}
      </RenderWhen>

      <ModalDialog
        show={showContactTicketDialog}
        size={'sm'}
        content={HPContactRequestEditor}
        contentProps={{
          isVisible: showContactTicketDialog,
          currentHpContactRequest: (currentHPContact || {}) as IFormJBPHPContactChangeRequest,

          partnerId: partnerId as string,
          leadId: leadPbmId,
          currentRole: currentHPContact?.Roles || '',

          onApply: async (newRequest: IFormJBPHPContactChangeRequest) => {
            setShowContactTicketDialog(false)
            setDuplicationCheckInProgress(true)

            const { hasDuplicate } = await saveHPContactChangeRequest({
              newRequest,
              resetCrudStorage,
              getContactsByEmail,
              jbpHpContactChangeRequestCrud,
              fetchPartnerRoles,
            })

            setDuplicationCheckInProgress(false)

            if (hasDuplicate) {
              setShowDuplicateDialog(true)
            } else {
              onUpdateWizard()
            }
          },
          onCancel: () => {
            setShowContactTicketDialog(false)
          },
        }}
        onClose={() => setShowContactTicketDialog(false)}
      />
      <HPContactsDuplicateDialog
        isShow={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
      />
    </div>
  )
}
